import { Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material';
import UserAvatar from '../shared/UserAvatar';
import { useMeCore } from '../../../core/modules/me';
import { Comment as UserComment } from '../../../core/interfaces/comment.interface';
import ReplyIcon from '@mui/icons-material/Reply';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachmentIcon from '@mui/icons-material/AttachFile';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import AddComment from './AddComment';
import { useCourseCore } from '../../../core/modules/course';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import useThemeMediaQuery from '../../../core/hooks/useThemeMediaQuery';

interface Props {
  comment: UserComment;
  classId: string;
  depth?: number;
  isMentor?: boolean;
  parentAuthorName?: string;
}

const Comment = ({ comment, classId, depth = 0, isMentor, parentAuthorName }: Props) => {
  const { t } = useTranslation();
  const { user } = useMeCore();
  const { course, getClassComments, deleteClassComment } = useCourseCore();
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [reply, setReply] = useState(false);

  const onSuccess = useCallback(() => {
    setReply(false);
  }, []);

  const onDelete = useCallback(() => {
    Swal.fire({
      title: t('alert.sure.title'),
      text: `Eliminar el comentario ${comment.content}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t('alert.sure.confirm.button'),
      cancelButtonText: t('alert.sure.cancel.button'),
    }).then((result) => {
      if (result.isConfirmed) {
        deleteClassComment(
          course?.id!,
          classId,
          comment.id,
          () => {
            getClassComments(course?.id!, classId);
          }
        );
      }
    });
  }, [t, comment.content, comment.id, deleteClassComment, course?.id, classId, getClassComments]);

  const canDelete = useMemo(() => {
    if (user?.userRole.student) {
      if (comment.user.id !== user?.id) {
        return false;
      }
      return comment.replies.length === 0;
    }

    return true;
  }, [comment.replies.length, comment.user.id, user?.id, user?.userRole.student]);

  const getRoleLabel = useCallback((target = comment.user) => {
    if (target?.role === 'admin') return 'Administrador';
    if (target?.role === 'professor') return 'Mentor';
    return 'Alumno';
  }, [comment.user.role]);

  const isMentorComment = useMemo(() => {
    if (typeof isMentor === 'boolean') {
      return isMentor;
    }
    return Boolean(comment.user?.userRole?.admin || comment.user?.userRole?.professor);
  }, [comment.user?.userRole?.admin, comment.user?.userRole?.professor, isMentor]);

  const roleLabel = getRoleLabel();
  const avatarSize = isMobile ? (depth ? 26 : 30) : depth ? 30 : 36;
  // gap entre avatar y texto: un poco menor para respuestas
  const gapPx = (depth ? 0.75 : 1.5) * 8;
  // Para la línea de respuestas, usar el tamaño del avatar del padre (sin depth)
  const parentAvatarSize = isMobile ? 30 : 36;
  const parentGapPx = 1.5 * 8;
  const lineLeft = parentAvatarSize + parentGapPx;

  return (
    <Box
      sx={{
        position: 'relative',
        p: 0,
        borderRadius: 0,
        border: 'none',
        ml: 0,
        boxShadow: 'none',
        maxWidth: '100%',
        width: '100%',
      }}
    >
      <Box display="flex" minWidth={0} alignItems="flex-start">
        <UserAvatar
          alt={comment.user.name || comment.user.email || 'user'}
          src={comment.user.avatar}
          name={comment.user.name}
          email={comment.user.email}
          sx={{
            width: avatarSize,
            height: avatarSize,
            flexShrink: 0,
            fontSize: `max(${Math.round(avatarSize * 0.36)}px, 0.55rem)`,
          }}
        />
        <Box flex={1} minWidth={0}>
          <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {comment.user.name || comment.user.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {moment(comment.createdAt).format('DD/MM/YYYY HH:mm')} • {moment(comment.createdAt).fromNow()}
            </Typography>
          </Box>
          {parentAuthorName ? (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.25,
                color: '#64748b',
                fontWeight: 600,
              }}
            >
              <ReplyIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
              En respuesta a{' '}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {parentAuthorName}
              </Box>
            </Typography>
          ) : null}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 1 }}>
            {comment.content}
          </Typography>

          {comment.attachments?.length ? (
            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
              {comment.attachments.map((attachment) => (
                <Button
                  key={attachment.id}
                  size="small"
                  variant="outlined"
                  startIcon={<AttachmentIcon fontSize="small" />}
                  href={attachment.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {attachment.name}
                </Button>
              ))}
            </Stack>
          ) : null}

          <Box display="flex" gap={1} mt={1.5} alignItems="center">
            {isMobile ? (
              <>
                {canDelete ? (
                  <IconButton size="small" color="error" onClick={onDelete} aria-label="Eliminar comentario">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                ) : null}
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setReply(!reply)}
                  aria-label="Responder comentario"
                >
                  <ReplyIcon fontSize="small" />
                </IconButton>
              </>
            ) : (
              <>
                {canDelete ? (
                  <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={onDelete}>
                    Eliminar
                  </Button>
                ) : null}
                <Button size="small" color="primary" startIcon={<ReplyIcon />} onClick={() => setReply(!reply)}>
                  Responder
                </Button>
              </>
            )}
          </Box>

          {reply ? <AddComment classId={classId} commentId={comment.id} onSuccess={onSuccess} /> : null}

          {comment.replies?.length ? (
            <Stack
              spacing={0.75}
              mt={0.5}
              width="100%"
              position="relative"
            >
              {comment.replies.map((child) => (
                  <Comment
                    key={child.id}
                    comment={child}
                    classId={classId}
                    depth={depth + 1}
                    isMentor={Boolean(child.user?.userRole?.admin || child.user?.userRole?.professor)}
                    parentAuthorName={comment.user?.name || comment.user?.email}
                  />
              ))}
            </Stack>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default Comment;
